import { environment } from '../../environments/environment';
import { EcommerceOrderItem, SearchEngineProduct } from '../models';
import { CheckoutItem, ShoppingCartUpdateItem } from '../services/analytics/analytics.service';
import { ShoppingCartItemProduct } from '../services/shopping-carts/shopping-carts.service';

export function mapShoppingCartItemProductsToCheckoutItems(shoppingCartItems: ShoppingCartItemProduct[]): CheckoutItem[] {
  return shoppingCartItems.map(mapShoppingCartItemProductToCheckoutItem);
}

export function mapShoppingCartItemProductToCheckoutItem(shoppingCartItem: ShoppingCartItemProduct): CheckoutItem {
  if (!shoppingCartItem.resource) {
    throw new Error('Shopping cart item product resource is required');
  }
  if (shoppingCartItem.resourceType === 'product') {
    return mapSearchEngineProductToCheckoutItem(shoppingCartItem.resource, shoppingCartItem.quantity);
  }
  return {
    discount: 0,
    free_shipping: false,
    item_brand: '',
    item_category: '',
    item_id: shoppingCartItem.resourceId,
    item_name: '',
    price: 0,
    quantity: shoppingCartItem.quantity,
    subtotal: 0,
  };
}

export function mapSearchEngineProductToCheckoutItem(searchEngineProduct: SearchEngineProduct, quantity: number = 1): CheckoutItem {
  return {
    discount: searchEngineProduct.discountAmount,
    free_shipping: !!searchEngineProduct.additionalData?.['freeShipping'],
    item_brand: searchEngineProduct.brand,
    item_category: searchEngineProduct.categories[0],
    ...(searchEngineProduct.categories[1] && { item_category2: searchEngineProduct.categories[1] }),
    ...(searchEngineProduct.categories[2] && { item_category3: searchEngineProduct.categories[2] }),
    ...(searchEngineProduct.categories[3] && { item_category4: searchEngineProduct.categories[3] }),
    ...(searchEngineProduct.categories[4] && { item_category5: searchEngineProduct.categories[4] }),
    item_id: searchEngineProduct.id,
    item_name: searchEngineProduct.name,
    price: searchEngineProduct.price,
    quantity,
    subtotal: searchEngineProduct.price * quantity,
  };
}

export function mapEcommerceOrderItemsToCheckoutItems(ecommerceOrderItems: EcommerceOrderItem[]): CheckoutItem[] {
  return ecommerceOrderItems.map(mapEcommerceOrderItemToCheckoutItem);
}

export function mapEcommerceOrderItemToCheckoutItem(ecommerceOrderItem: EcommerceOrderItem): CheckoutItem {
  return {
    discount: ecommerceOrderItem.discountAmount,
    free_shipping: ecommerceOrderItem.shippingCost === 0,
    item_brand: ecommerceOrderItem.brand,
    item_category: ecommerceOrderItem.categories[0],
    ...(ecommerceOrderItem.categories[1] && { item_category2: ecommerceOrderItem.categories[1] }),
    ...(ecommerceOrderItem.categories[2] && { item_category3: ecommerceOrderItem.categories[2] }),
    ...(ecommerceOrderItem.categories[3] && { item_category4: ecommerceOrderItem.categories[3] }),
    ...(ecommerceOrderItem.categories[4] && { item_category5: ecommerceOrderItem.categories[4] }),
    item_id: ecommerceOrderItem.id,
    item_name: ecommerceOrderItem.name,
    price: ecommerceOrderItem.price,
    quantity: ecommerceOrderItem.quantity,
    subtotal: ecommerceOrderItem.price * ecommerceOrderItem.quantity,
  };
}

export function mapShoppingCartItemProductsToCartUpdateItems(shoppingCartItems: ShoppingCartItemProduct[]): ShoppingCartUpdateItem[] {
  return shoppingCartItems.map(mapShoppingCartItemProductToCartUpdateItem);
}

export function mapShoppingCartItemProductToCartUpdateItem(shoppingCartItem: ShoppingCartItemProduct): ShoppingCartUpdateItem {
  if (!shoppingCartItem.resource) {
    throw new Error('Shopping cart item product resource is required');
  }
  if (shoppingCartItem.resourceType === 'product') {
    const data = mapSearchEngineProductToCheckoutItem(shoppingCartItem.resource, shoppingCartItem.quantity);
    return {
      ...data,
      image_url: shoppingCartItem.resource.images.length ? shoppingCartItem.resource.images[0] : '',
      product_url: `${environment.appUrl}/ecommerce/products/${shoppingCartItem.resource.slug}`,
    };
  }
  return {
    discount: 0,
    free_shipping: false,
    image_url: '',
    item_brand: '',
    item_category: '',
    item_id: shoppingCartItem.resourceId,
    item_name: '',
    price: 0,
    product_url: '',
    quantity: shoppingCartItem.quantity,
    subtotal: 0,
  };
}